import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { PaginationState } from '@tanstack/react-table';
import { Plus } from 'lucide-react';

import { toast } from 'sonner';
import { Button } from '@r/components/ui/button.tsx';
import { CreateCustomShopDialog } from '@admin/components/shops/create-custom-shop-dialog.tsx';

import {
  apiShopsGetCollection,
  getApiShopsGetCollectionQueryKey
} from '@r/api/generated/client.ts';
import type { ApiShopsGetCollectionParams } from '@r/api/generated/client.schemas.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';

import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { useUser } from '@r/api/auth.tsx';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { isStepLogicType } from '@admin/types/workspace/logic.ts';
import { useShopColumns } from '@admin/components/shops/shop-columns.tsx';
import type { ShopShopReadModel } from '@r/api/generated/client.schemas.ts';
import { getActiveWorkspaceUniversal } from '@r/helpers/api.ts';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { isAdminOrHigher, type Role } from '@r/utils/roles.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';

export default function ShopList() {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const user = useUser();
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const activeWorkspace = getActiveWorkspaceUniversal(user.data?.visibleWorkspaces ?? []);
  const { workspace } = UseWorkspace(user.data);
  const isStepProgram = isStepLogicType(workspace?.currentLogicWorkspace?.logic?.type);
  const canCreateCustomShop = isAdminOrHigher(activeWorkspace?.maxRole as Role) && !isStepProgram;

  // État local pour les données et la pagination (pattern similaire à orders.tsx)
  const initialShops: ShopShopReadModel[] = [];
  const [shopsData, setShopsData] = useState<ShopShopReadModel[]>(initialShops);
  const [totalPages, setTotalPages] = useState(1);

  const shopsTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('shops', user.data?.id),
    [user.data?.id]
  );

  // Créer le tableau avec les données de l'état local
  const { table } = useDataTable({
    data: shopsData,
    columns: useShopColumns(),
    pageCount: totalPages,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: shopsTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;

  const shopsParams: ApiShopsGetCollectionParams = {
    page: pageIndex + 1,
    perPage: pageSize
  };

  const shopsListQuery = useQuery({
    queryKey: getApiShopsGetCollectionQueryKey(shopsParams),
    queryFn: async () => {
      try {
        const shops = await apiShopsGetCollection(shopsParams);

        // Ajouter la propriété url pour la navigation interne
        const shopsWithUrl = (shops.data ?? []).map((shop: ShopShopReadModel) => ({
          ...shop,
          url: `/shops/${shop.id}`
        }));

        return {
          ...shops,
          data: shopsWithUrl
        };
      } catch (error) {
        if (isOrvalErrorFromPath(error, 'get', '/shops')) {
          showOrvalErrorToast(error, t('toast.error.label'));
        } else {
          toast.error(t('toast.error.label'));
        }
        throw error;
      }
    },
    placeholderData: (previousData) => previousData
  });

  // Mettre à jour les données et la pagination quand la query change (pattern orders.tsx)
  useEffect(() => {
    if (shopsListQuery.data) {
      setShopsData(shopsListQuery.data.data);
      setTotalPages(shopsListQuery.data.pagination?.last ?? 1);
    }
  }, [shopsListQuery.data]);

  return (
    <>
      <div className="flex flex-wrap gap-4 justify-between items-center mb-10">
        <span className="text-4xl font-bold">{t('admin.shops.title')}</span>

        {canCreateCustomShop && (
          <Button rounded="full" type="button" onClick={() => setIsCreateDialogOpen(true)}>
            {t('admin.shops.create.button')} <Plus />
          </Button>
        )}
      </div>

      {canCreateCustomShop && (
        <CreateCustomShopDialog isOpen={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen} />
      )}

      <div className="mt-12">
        {shopsListQuery.isLoading && (
          <DataTableSkeleton
            columnCount={10}
            cellWidths={['10rem', '40rem', '12rem', '12rem', '8rem']}
          />
        )}

        {shopsListQuery.isSuccess && (
          <DataTable table={table} totalItems={shopsListQuery.data?.pagination?.totalItems} />
        )}
      </div>
    </>
  );
}
